/**
 * OfflineModeIndicator - Shows when circuit breaker is open or service degraded
 */
import React, { useEffect, useState } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { getCircuitBreakerState, resetCircuitBreaker } from '../utils/retryMechanism';

const OfflineModeIndicator = () => {
  const [circuitState, setCircuitState] = useState(null);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    // Check circuit breaker state periodically
    const checkCircuitState = () => {
      const state = getCircuitBreakerState();
      setCircuitState(state);
    };

    checkCircuitState();
    const interval = setInterval(checkCircuitState, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleManualReset = async () => {
    setIsResetting(true);
    try {
      resetCircuitBreaker();
      // Wait a moment before checking state
      await new Promise(resolve => setTimeout(resolve, 1000));
      const state = getCircuitBreakerState();
      setCircuitState(state);
    } catch (error) {
      console.error('Failed to reset circuit breaker:', error);
    } finally {
      setIsResetting(false);
    }
  };

  // Only show when circuit is open
  if (!circuitState || circuitState?.state !== 'open') {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-red-600 text-white px-4 py-3 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <WifiOff className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold">
                Service Temporarily Unavailable
              </p>
              <p className="text-xs opacity-90">
                We're experiencing connectivity issues. Some features may not work properly.
              </p>
            </div>
          </div>
          <button
            onClick={handleManualReset}
            disabled={isResetting}
            className="flex items-center gap-2 px-4 py-2 bg-white text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${isResetting ? 'animate-spin' : ''}`} />
            {isResetting ? 'Reconnecting...' : 'Reconnect'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfflineModeIndicator;
